import { ArrowDownwardOutlined, ArrowUpwardOutlined } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { isMobile } from 'react-device-detect';
import { getDepthValue, getDepthValueFromField } from '../../../../utils/depthSummaryExtractor';

export const columns = (mudDisplacementUnit, plotFor, sbpField) => [
  {
    field: 'strokes',
    headerName: 'Strokes Count',
    renderHeader: () => (
      <Box>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          Strokes
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          count
        </Typography>
      </Box>
    ),
    width: 70,
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: 'flow_rate',
    headerName: 'Rate',
    renderHeader: () => (
      <Box>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          Rate
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          ({mudDisplacementUnit?.flow_rate?.label})
        </Typography>
      </Box>
    ),
    width: 65,
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: 'volume',
    headerName: 'Volume',
    renderHeader: () => (
      <Box>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          Volume
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          ({mudDisplacementUnit?.volume?.label})
        </Typography>
      </Box>
    ),
    width: 75,
    valueFormatter: (params) => {
      if (params.value === null || params.value === undefined || params.value === '') {
        return '';
      }
      const value = Number(params.value);
      return isNaN(value) ? params.value : value.toFixed(mudDisplacementUnit?.volume?.decimals);
    },
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: 'front_position',
    headerName: 'Front Depth',
    renderHeader: () => (
      <Box>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          Front
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          Depth ({mudDisplacementUnit?.front_position?.label})
        </Typography>
      </Box>
    ),
    width: 80,
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: 'front_direction',
    headerName: 'Front Direction',
    renderHeader: () => (
      <Box>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          Front
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          Direction
        </Typography>
      </Box>
    ),
    width: 75,
    headerAlign: 'center',
    align: 'center',
    renderCell: (params) => {
      if (params?.row?.front_direction === 'down') {
        return (
      <ArrowDownwardOutlined
        fontSize={isMobile ? 'small' : 'medium'}
        sx={{ color: 'text.secondary', verticalAlign: 'middle' }}
      />
        );
      }
      if (params?.row?.front_direction === 'up') {
        return (
      <ArrowUpwardOutlined
        fontSize={isMobile ? 'small' : 'medium'}
        sx={{ color: 'text.secondary', verticalAlign: 'middle' }}
      />
        );
      }
      return null;
    }
  },
  {
    field: `icp_${plotFor.toLowerCase()}`,
    headerName: `CS ${plotFor}`,
    renderHeader: () => (
      <Box>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          {`CS ${plotFor}`}
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          ({mudDisplacementUnit?.icp_ecd?.label})
        </Typography>
      </Box>
    ),
    width: 70,
    headerAlign: 'center',
    align: 'center',
    valueGetter: ({ row }) => getDepthValue(row, 'casing_shoe', plotFor),
  },
  {
    field: `lp_${plotFor.toLowerCase()}`,
    headerName: `LP ${plotFor}`,
    renderHeader: () => (
      <Box>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          {`LP ${plotFor}`}
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          ({mudDisplacementUnit?.lp_ecd?.label})
        </Typography>
      </Box>
    ),
    width: 70,
    headerAlign: 'center',
    align: 'center',
    valueGetter: ({ row }) => getDepthValue(row, 'landing_point', plotFor),
  },
  {
    field: `bh_${plotFor.toLowerCase()}`,
    headerName: `BH ${plotFor}`,
    renderHeader: () => (
      <Box>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          {`BH ${plotFor}`}
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          ({mudDisplacementUnit?.bh_ecd?.label})
        </Typography>
      </Box>
    ),
    width: 65,
    headerAlign: 'center',
    align: 'center',
    valueGetter: ({ row }) => getDepthValue(row, 'hole_depth', plotFor),
  },
  {
    field: 'sbp',
    headerName: 'Dynamic SBP',
    renderHeader: () => (
      <Box>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          Dynamic
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          SBP ({mudDisplacementUnit?.sbp?.label})
        </Typography>
      </Box>
    ),
    valueFormatter: (params) => {
      if (params.value === null || params.value === undefined || params.value === '') {
        return '';
      }
      const value = Number(params.value);
      return isNaN(value) ? params.value : value.toFixed(mudDisplacementUnit?.sbp?.decimals);
    },
    width: 75,
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: sbpField,
    headerName: 'Connection SBP',
    renderHeader: () => (
      <Box>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          Connection
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          SBP ({mudDisplacementUnit?.connection_sbp?.label})
        </Typography>
      </Box>
    ),
    width: 100,
    valueFormatter: (params) => {
      if (params.value === null || params.value === undefined || params.value === '') {
        return '';
      }
      const value = Number(params.value);
      return isNaN(value) ? params.value : value.toFixed(mudDisplacementUnit?.connection_sbp?.decimals);
    },
    headerAlign: 'center',
    align: 'center',
  },
];

export const customColumns = (mudDisplacementUnit, plotFor, sbpField) => [
  {
    field: 'strokes',
    headerName: 'Strokes Count',
    renderHeader: () => (
      <Box>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          Strokes
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          count
        </Typography>
      </Box>
    ),
    width: 70,
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: 'flow_rate',
    headerName: 'Rate',
    renderHeader: () => (
      <Box>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          Rate
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          ({mudDisplacementUnit?.flow_rate?.label})
        </Typography>
      </Box>
    ),
    width: 65,
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: 'volume',
    headerName: 'Volume',
    renderHeader: () => (
      <Box>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          Volume
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          ({mudDisplacementUnit?.volume?.label})
        </Typography>
      </Box>
    ),
    width: 75,
    valueFormatter: (params) => {
      if (params.value === null || params.value === undefined || params.value === '') {
        return '';
      }
      const value = Number(params.value);
      return isNaN(value) ? params.value : value.toFixed(mudDisplacementUnit?.volume?.decimals);
    },
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: 'front_position',
    headerName: 'Front Depth',
    renderHeader: () => (
      <Box>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          Front
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          Depth ({mudDisplacementUnit?.front_position?.label})
        </Typography>
      </Box>
    ),
    width: 80,
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: 'front_direction',
    headerName: 'Front Direction',
    renderHeader: () => (
      <Box>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          Front
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          Direction
        </Typography>
      </Box>
    ),
    width: 75,
    headerAlign: 'center',
    align: 'center',
    renderCell: (params) => {
      if (params?.row?.front_direction === 'down') {
        return (
      <ArrowDownwardOutlined
        fontSize={isMobile ? 'small' : 'medium'}
        sx={{ color: 'text.secondary', verticalAlign: 'middle' }}
      />
        );
      }
      if (params?.row?.front_direction === 'up') {
        return (
      <ArrowUpwardOutlined
        fontSize={isMobile ? 'small' : 'medium'}
        sx={{ color: 'text.secondary', verticalAlign: 'middle' }}
      />
        );
      }
      return null;
    }
  },
  {
    field: plotFor?.field[0],
    headerName: `ESD @ ${plotFor?.label}`,
    renderHeader: () => (
      <Box>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
         {`ESD @ ${plotFor?.label}`}
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          ({mudDisplacementUnit?.icp_ecd?.label})
        </Typography>
      </Box>
    ),
    width: 145,
    headerAlign: 'center',
    align: 'center',
    valueGetter: ({ row }) => getDepthValueFromField(row, plotFor?.field[0]),
  },
  {
    field: plotFor?.field[1],
    headerName: `ECD @ ${plotFor?.label}`,
    renderHeader: () => (
      <Box>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          {`ECD @ ${plotFor?.label}`}
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          ({mudDisplacementUnit?.lp_ecd?.label})
        </Typography>
      </Box>
    ),
    width: 145,
    headerAlign: 'center',
    align: 'center',
    valueGetter: ({ row }) => getDepthValueFromField(row, plotFor?.field[1]),
  },
  {
    field: 'sbp',
    headerName: 'Dynamic SBP',
    renderHeader: () => (
      <Box>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          Dynamic
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          SBP ({mudDisplacementUnit?.sbp?.label})
        </Typography>
      </Box>
    ),
    valueFormatter: (params) => {
      if (params.value === null || params.value === undefined || params.value === '') {
        return '';
      }
      const value = Number(params.value);
      return isNaN(value) ? params.value : value.toFixed(mudDisplacementUnit?.sbp?.decimals);
    },
    width: 75,
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: sbpField,
    headerName: 'Connection SBP',
    renderHeader: () => (
      <Box>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          Connection
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          SBP ({mudDisplacementUnit?.connection_sbp?.label})
        </Typography>
      </Box>
    ),
    width: 100,
    valueFormatter: (params) => {
      if (params.value === null || params.value === undefined || params.value === '') {
        return '';
      }
      const value = Number(params.value);
      return isNaN(value) ? params.value : value.toFixed(mudDisplacementUnit?.connection_sbp?.decimals);
    },
    headerAlign: 'center',
    align: 'center',
  },
];
